import { useMemo, useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { RESIDENCY_OPTIONS, DEFAULTS, formatAED, clamp, computeMortgage } from '../../utils/mortgageCalculator'

const getRangeFillPercent = (value, min, max) => {
    if (max === min) return 0
    return ((Math.min(Math.max(Number(value), min), max) - min) / (max - min)) * 100
}

const RangeInput = ({ min, max, step, value, onChange, fillPercent }) => (
    <div className="custom-range-track-wrap">
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={onChange}
            className="custom-range w-100"
            style={{ '--range-fill': `${fillPercent}%` }}
        />
    </div>
)

const MortgageCalculatorWidget = () => {
    const [residency, setResidency] = useState('uae_resident')
    const [propertyValue, setPropertyValue] = useState(DEFAULTS.propertyValue)
    const [interestRate, setInterestRate] = useState(DEFAULTS.interestRate)
    const [loanYears, setLoanYears] = useState(DEFAULTS.loanYears)

    // 1. Compute dynamic minimum down payment floor
    const minDownPct = useMemo(() => {
        if (residency === 'non_resident') {
            return 40
        }

        const isAboveThreshold = propertyValue >= 5000000

        if (residency === 'uae_national') {
            return isAboveThreshold ? 25 : 15
        }

        return isAboveThreshold ? 30 : 20
    }, [residency, propertyValue])

    const maxDownPct = 80

    // 2. Initialize state safety catch
    const [downPaymentPercent, setDownPaymentPercent] = useState(() => {
        if (residency === 'non_resident') return 40

        const isAboveThreshold = DEFAULTS.propertyValue >= 5000000
        if (residency === 'uae_national') {
            return isAboveThreshold ? 25 : 15
        }
        return isAboveThreshold ? 30 : 20
    })

    // 3. Keep state aggressively tethered to minimum constraints
    useEffect(() => {
        setDownPaymentPercent((prev) => Math.max(prev, minDownPct))
    }, [minDownPct])

    const applyResidency = useCallback(
        (id) => {
            setResidency(id)

            let targetMin = 20
            if (id === 'non_resident') {
                targetMin = 40
            } else {
                const isAboveThreshold = propertyValue >= 5000000
                targetMin = id === 'uae_national' ? (isAboveThreshold ? 25 : 15) : isAboveThreshold ? 30 : 20
            }

            setDownPaymentPercent(targetMin)
        },
        [propertyValue],
    )

    const setDownPaymentFromPercent = (pct) => {
        const clamped = clamp(pct, minDownPct, maxDownPct)
        setDownPaymentPercent(clamped)
    }

    const safePropertyValue = propertyValue < DEFAULTS.propertyMin ? DEFAULTS.propertyMin : propertyValue

    const activeDownPaymentPercent = Math.max(downPaymentPercent, minDownPct)

    const results = useMemo(
        () =>
            computeMortgage({
                propertyValue: safePropertyValue,
                downPaymentPercent: activeDownPaymentPercent,
                interestRate,
                loanYears,
            }),
        [safePropertyValue, activeDownPaymentPercent, interestRate, loanYears],
    )

    return (
        <form action="#" className="position-relative z-1 mortgage-calc-form">
            <div
                className="row g-0 overflow-hidden align-items-stretch mortgage-calc-card"
                style={{
                    borderRadius: '32px',
                    border: '3px solid #000',
                    background: '#fff',
                }}
            >
                {/* LEFT SIDE */}
                <div className="col-lg-7 p-4 p-lg-5 mortgage-calc-inputs">
                    {/* RESIDENCY */}
                    <div className="mb-4">
                        <label className="fw-semibold mb-2 d-block">Residency Status</label>

                        <div
                            className="p-2 d-grid"
                            style={{
                                gridTemplateColumns: 'repeat(3,1fr)',
                                background: '#F5F5F5',
                                borderRadius: '8px',
                                gap: '6px',
                            }}
                        >
                            {RESIDENCY_OPTIONS.map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => applyResidency(opt.id)}
                                    className="border-0 fw-medium fs-ss-12-mob px-2 px-md-0"
                                    style={{
                                        height: '44px',
                                        paddingTop: '5px',
                                        borderRadius: '8px',
                                        background: residency === opt.id ? '#fff' : 'transparent',
                                        boxShadow: residency === opt.id ? '0 4px 14px rgba(0,0,0,0.08)' : 'none',
                                        transition: '0.3s ease',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* PROPERTY VALUE */}
                    <InputBox
                        label="Property Price"
                        value={propertyValue}
                        onChange={setPropertyValue}
                        min={DEFAULTS.propertyMin}
                        max={DEFAULTS.propertyMax}
                        step={10000}
                    />

                    {/* DOWN PAYMENT */}
                    <DownPaymentInputBox
                        label="Down Payment"
                        value={Math.round(results.downPayment)}
                        propertyValue={propertyValue}
                        onChangePercent={setDownPaymentFromPercent}
                        minPercent={minDownPct}
                        maxPercent={maxDownPct}
                        percentValue={activeDownPaymentPercent}
                    />

                    {/* LOAN YEARS */}
                    <SliderBox
                        label="Loan Duration"
                        value={loanYears}
                        min={DEFAULTS.loanYearsMin}
                        max={DEFAULTS.loanYearsMax}
                        suffix="Year"
                        onChange={setLoanYears}
                    />

                    {/* INTEREST */}
                    <SliderBox
                        label="Interest Rate"
                        value={interestRate}
                        min={DEFAULTS.interestMin}
                        max={DEFAULTS.interestMax}
                        step={0.1}
                        suffix="%"
                        onChange={setInterestRate}
                    />
                </div>

                {/* RIGHT SIDE — background stretches full card height */}
                <div className="col-lg-5 p-4 p-lg-5 d-flex flex-column mortgage-calc-results">
                    <div className="mb-4 text-center text-md-end">
                        <p className="text-secondary mb-2 fw-medium">Loan Amount</p>
                        <h2 className="fw-bold mb-0" style={{ fontSize: 'clamp(1.7rem,3vw,2rem)' }}>
                            {formatAED(results.loanAmount)} AED
                        </h2>
                    </div>

                    <div className="mb-4 text-center text-md-end">
                        <p className="text-secondary mb-2 fw-medium">Monthly Payment</p>
                        <h2 className="fw-bold mb-0" style={{ fontSize: 'clamp(1.7rem,3vw,2rem)' }}>
                            {formatAED(Math.round(results.monthlyPayment))} AED
                        </h2>
                    </div>

                    <div
                        className="my-4"
                        style={{
                            width: '100%',
                            height: '1px',
                            background: '#e5e5e5',
                        }}
                    />

                    <div className="d-flex flex-column gap-4">
                        <div>
                            <BreakdownRow label="Principal" value={results.principalPortion} />
                            <BreakdownRow label="Interest" value={results.interestPortion} />
                            <BreakdownRow label="Interest Rate" value={`${interestRate.toFixed(2)}%`} plain />
                            <BreakdownRow
                                label="Loan Period"
                                value={`${loanYears} ${loanYears === 1 ? 'Year' : 'Years'}`}
                                plain
                            />
                        </div>

                        <p className="text-center text-secondary small mb-0">
                            Results are indicative and based on the details entered. Actual rates and terms will vary by
                            bank — speak to a mortgage advisor for a personalized quote.
                        </p>

                        <Link to="/contact-us" className="text-decoration-none">
                            <button
                                type="button"
                                className="w-100 border-0 text-white fw-semibold"
                                style={{
                                    height: '58px',
                                    borderRadius: '14px',
                                    background: '#000',
                                    fontSize: '16px',
                                }}
                            >
                                Contact Us
                            </button>
                        </Link>
                    </div>
                </div>
            </div>
        </form>
    )
}

const InputBox = ({ label, value, onChange, min = 0, max = 5000000, step = 10000 }) => {
    const safeValue = Math.min(Math.max(Number(value), min), max)
    const percentage = getRangeFillPercent(safeValue, min, max)

    return (
        <div className="mb-4">
            <label className="fw-semibold mb-1 d-block">{label}</label>

            <div
                style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: '#fff',
                }}
            >
                <div
                    className="d-flex align-items-center mx-2 mt-2 mb-1 border rounded-3 overflow-hidden"
                    style={{
                        background: '#fafafa',
                        height: '44px',
                    }}
                >
                    <input
                        type="text"
                        inputMode="decimal"
                        value={
                            value === '' || value === null || value === undefined
                                ? ''
                                : Number(value).toLocaleString('en-US')
                        }
                        onBlur={() => {
                            const num = Number(String(value).replace(/,/g, ''))
                            if (!isNaN(num)) {
                                const clamped = num < min ? min : num > max ? max : num
                                onChange(clamped)
                            }
                        }}
                        onChange={(e) => {
                            const raw = e.target.value.replace(/,/g, '')
                            if (raw === '') {
                                onChange(0)
                                return
                            }
                            const num = Number(raw)
                            if (!isNaN(num)) {
                                onChange(num)
                            }
                        }}
                        className="form-control border-0 shadow-none h-100"
                        style={{
                            fontSize: '16px',
                            background: 'transparent',
                        }}
                    />

                    <div
                        className="d-flex align-items-center justify-content-center px-4 h-100 pt-2"
                        style={{
                            borderLeft: '1px solid #dee2e6',
                            minWidth: '90px',
                            fontWeight: '500',
                            background: '#fafafa',
                        }}
                    >
                        AED
                    </div>
                </div>

                <div className="px-2 pb-1">
                    <RangeInput
                        min={min}
                        max={max}
                        step={step}
                        value={safeValue}
                        fillPercent={percentage}
                        onChange={(e) => onChange(Number(e.target.value))}
                    />
                </div>

                <div className="d-flex justify-content-between px-2 pb-2 fs-ss-12">
                    <small style={{ color: '#707070' }}>{formatAED(min)} AED</small>
                    <small style={{ color: '#707070' }}>{formatAED(max)} AED</small>
                </div>
            </div>
        </div>
    )
}

const DownPaymentInputBox = ({
    label,
    value,
    propertyValue,
    onChangePercent,
    minPercent,
    maxPercent,
    percentValue,
}) => {
    const safePercentValue = Math.min(Math.max(percentValue, minPercent), maxPercent)
    const percentage = getRangeFillPercent(safePercentValue, minPercent, maxPercent)
    const minAED = (propertyValue * minPercent) / 100
    const maxAED = (propertyValue * maxPercent) / 100

    return (
        <div className="mb-4">
            <label className="fw-semibold mb-2 d-block">{label}</label>

            <div
                style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: '#fff',
                }}
            >
                <div
                    className="d-flex align-items-center mx-2 mt-2 mb-1 border rounded-3 overflow-hidden"
                    style={{
                        background: '#fafafa',
                        height: '44px',
                    }}
                >
                    <input
                        type="text"
                        inputMode="decimal"
                        value={
                            value === '' || value === null || value === undefined
                                ? ''
                                : Number(value).toLocaleString('en-US')
                        }
                        onBlur={() => {
                            const num = Number(String(value).replace(/,/g, ''))
                            if (!isNaN(num)) {
                                const targetPct = (num / propertyValue) * 100
                                onChangePercent(targetPct)
                            }
                        }}
                        onChange={(e) => {
                            const raw = e.target.value.replace(/,/g, '')
                            if (raw === '') {
                                onChangePercent(minPercent)
                                return
                            }
                            const num = Number(raw)
                            if (!isNaN(num)) {
                                const targetPct = (num / propertyValue) * 100
                                onChangePercent(targetPct)
                            }
                        }}
                        className="form-control border-0 shadow-none h-100"
                        style={{
                            fontSize: '16px',
                            background: 'transparent',
                        }}
                    />

                    <div
                        className="d-flex align-items-center justify-content-center px-4 h-100 pt-2"
                        style={{
                            borderLeft: '1px solid #dee2e6',
                            minWidth: '90px',
                            fontWeight: '500',
                            background: '#fafafa',
                        }}
                    >
                        {Math.round(safePercentValue)}%
                    </div>
                </div>

                <div className="px-2 pb-1">
                    <RangeInput
                        min={minPercent}
                        max={maxPercent}
                        step={0.5}
                        value={safePercentValue}
                        fillPercent={percentage}
                        onChange={(e) => onChangePercent(Number(e.target.value))}
                    />
                </div>

                <div className="d-flex justify-content-between px-2 pb-2 fs-ss-12">
                    <small style={{ color: '#707070' }}>
                        {formatAED(Math.round(minAED))} AED ({minPercent}%)
                    </small>
                    <small style={{ color: '#707070' }}>
                        {formatAED(Math.round(maxAED))} AED ({maxPercent}%)
                    </small>
                </div>
            </div>
        </div>
    )
}

const SliderBox = ({ label, value, min, max, step = 1, suffix, onChange }) => {
    const safeValue = Math.min(Math.max(Number(value), min), max)
    const percentage = getRangeFillPercent(safeValue, min, max)

    const formatSuffix = (val) => {
        if (suffix === 'Year') {
            return val === 1 ? 'Year' : 'Years'
        }
        return suffix
    }

    return (
        <div className="mb-2">
            <div className="d-flex">
                <label className="fw-semibold mb-1 d-block">{label}</label>
                <div className="ms-auto fs-ss-14 mt-1">
                    {safeValue} {formatSuffix(safeValue)}
                </div>
            </div>

            <div>
                <RangeInput
                    min={min}
                    max={max}
                    step={step}
                    value={safeValue}
                    fillPercent={percentage}
                    onChange={(e) => onChange(Number(e.target.value))}
                />

                <div className="d-flex justify-content-between pb-3 fs-ss-12">
                    <small style={{ color: '#707070' }}>
                        {min} {formatSuffix(min)}
                    </small>
                    <small style={{ color: '#707070' }}>
                        {max} {formatSuffix(max)}
                    </small>
                </div>
            </div>
        </div>
    )
}

const BreakdownRow = ({ label, value, plain }) => (
    <div className="d-flex justify-content-between align-items-center py-2">
        <span className="text-secondary">{label}</span>
        <span className="fw-semibold">{plain ? value : `${formatAED(Math.round(value))} AED`}</span>
    </div>
)

export default MortgageCalculatorWidget
